import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { ArrowLeft, MapPin, BedDouble, Bath, Ruler, Phone, Mail, Building2, X, Check, Calculator } from "lucide-react";

function fmtPrice(price, currency) {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency", currency: currency || "EUR",
      maximumFractionDigits: 0, currencyDisplay: "narrowSymbol",
    }).format(price);
  } catch {
    return `${currency || "EUR"} ${Number(price).toLocaleString()}`;
  }
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const lang = typeof window !== "undefined" ? (localStorage.getItem("lang") || "en") : "en";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/properties/${id}`);
        setP(data);
      } catch {
        toast.error("Property not found");
        navigate("/");
      }
    })();
  }, [id, navigate]);

  const submitLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { property_id: p.property_id, ...form });
      setSent(true);
      toast.success("Sent! The agent will contact you shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  if (!p) {
    return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  }

  const images = p.images && p.images.length ? p.images : ["/mascot.png"];
  const agent = p.agent || {};

  return (
    <div className="min-h-screen bg-[var(--paper)] grain">
      <header className="fixed top-0 left-0 right-0 z-30">
        <div className="flex items-center justify-between px-6 sm:px-10 py-5">
          <Link to="/" data-testid="detail-back" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold hover:underline underline-offset-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <span className="w-6 h-6 grid place-items-center bg-black text-white font-display font-black">Æ</span>
            <span className="font-display text-lg font-medium tracking-tight">Aegis Realtor</span>
          </Link>
          <div className="w-16" />
        </div>
      </header>

      <main className="pt-24 pb-20 px-6 sm:px-10 max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: gallery + details */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="aspect-[4/3] overflow-hidden bg-[var(--paper-2)] hairline"
            >
              <img
                data-testid="detail-main-image"
                src={images[active]}
                alt={p.title}
                className="w-full h-full object-cover"
              />
            </motion.div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.slice(0, 10).map((src, i) => (
                  <button
                    key={i}
                    data-testid={`detail-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`aspect-square overflow-hidden hairline ${active === i ? "ring-2 ring-black ring-offset-2" : "opacity-70 hover:opacity-100"}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-10">
              <div className="flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                <MapPin className="w-3 h-3" strokeWidth={1.75} />
                <span>{p.city}, {p.country}</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">{p.title}</h1>
              <div className="mt-4 font-display text-3xl font-semibold tabular-nums">{fmtPrice(p.price, p.currency)}</div>

              <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
                {p.bedrooms > 0 && <span className="flex items-center gap-2"><BedDouble className="w-4 h-4" /> {p.bedrooms} bedrooms</span>}
                {p.bathrooms > 0 && <span className="flex items-center gap-2"><Bath className="w-4 h-4" /> {p.bathrooms} bathrooms</span>}
                {p.area_sqm > 0 && <span className="flex items-center gap-2"><Ruler className="w-4 h-4" /> {p.area_sqm} m²</span>}
                {p.property_type && <span className="uppercase tracking-[0.2em] text-[10px] font-bold">{p.property_type}</span>}
              </div>

              <p className="mt-6 text-base leading-relaxed max-w-2xl">{p.description}</p>

              {p.purpose_tags?.length > 0 && (
                <div className="mt-6 flex gap-2 flex-wrap">
                  {p.purpose_tags.map((tag) => (
                    <span key={tag} className="text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 hairline">{tag}</span>
                  ))}
                </div>
              )}

              <MortgageCalculator lang={lang} defaultAmount={p.price} currency={p.currency || "EUR"} />
            </div>
          </div>

          {/* Right: agent contact card */}
          <aside data-testid="agent-card" className="lg:sticky lg:top-24 h-fit">
            <div className="hairline bg-white p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Listed by</div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/mascot.png" alt="" className="w-12 h-12 rounded-full hairline bg-[var(--paper-2)] object-cover" />
                <div>
                  <div className="font-display text-lg font-medium">{agent.name || "Aegis Agent"}</div>
                  {agent.agency && <div className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" /> {agent.agency}</div>}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {agent.email && (
                  <a data-testid="agent-email" href={`mailto:${agent.email}`} className="flex items-center gap-2 hover:underline underline-offset-4">
                    <Mail className="w-4 h-4" /> {agent.email}
                  </a>
                )}
                {agent.phone && (
                  <a data-testid="agent-phone" href={`tel:${agent.phone}`} className="flex items-center gap-2 hover:underline underline-offset-4">
                    <Phone className="w-4 h-4" /> {agent.phone}
                  </a>
                )}
                {!agent.email && !agent.phone && (
                  <div className="text-xs text-muted-foreground">Contact details available on request.</div>
                )}
              </div>

              <button
                data-testid="request-contact-btn"
                onClick={() => setShowContact(true)}
                className="mt-6 w-full py-3 bg-black text-white font-bold tracking-wide hover:opacity-90"
              >
                Ask agent to contact me
              </button>
            </div>
          </aside>
        </div>
      </main>

      {/* Contact request modal */}
      {showContact && (
        <div className="fixed inset-0 z-50 grid place-items-center px-6 bg-black/40 backdrop-blur-sm" onClick={() => !busy && setShowContact(false)}>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white hairline p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Request contact</div>
                <div className="font-display text-2xl font-medium">{p.title}</div>
              </div>
              <button data-testid="close-modal" onClick={() => !busy && setShowContact(false)} className="text-muted-foreground hover:text-black"><X className="w-4 h-4" /></button>
            </div>

            {sent ? (
              <div data-testid="lead-success" className="py-8 text-center">
                <div className="w-14 h-14 grid place-items-center mx-auto bg-black text-white mb-4"><Check className="w-6 h-6" /></div>
                <div className="font-display text-xl mb-2">Message sent</div>
                <p className="text-sm text-muted-foreground">The agent has been notified. They&apos;ll be in touch shortly.</p>
                <button onClick={() => setShowContact(false)} className="mt-6 px-5 py-2 hairline">Close</button>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-4">
                {["name", "email", "phone"].map((k) => (
                  <div key={k}>
                    <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{k}</label>
                    <input
                      data-testid={`lead-${k}`}
                      required
                      type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                      value={form[k]}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Message (optional)</label>
                  <textarea
                    data-testid="lead-message"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none"
                  />
                </div>
                <button
                  data-testid="lead-submit"
                  disabled={busy}
                  className="w-full py-3 bg-black text-white font-bold tracking-wide disabled:opacity-40"
                >
                  {busy ? "Sending…" : "Send request"}
                </button>
                <p className="text-[10px] text-muted-foreground text-center">
                  Your info is shared only with this listing&apos;s agent.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

function MortgageCalculator({ lang, defaultAmount, currency }) {
  const [amount, setAmount] = useState(defaultAmount || 300000);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(25);
  const [monthly, setMonthly] = useState(null);

  const calc = (e) => {
    e.preventDefault();
    const P = Number(amount);
    const n = Number(years) * 12;
    const r = Number(rate) / 100 / 12;
    if (!P || !n || r < 0) return;
    const m = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    setMonthly(m);
  };

  const fmtCur = (v) => {
    try {
      return new Intl.NumberFormat("en", { style: "currency", currency, maximumFractionDigits: 0, currencyDisplay: "narrowSymbol" }).format(v);
    } catch {
      return `${currency} ${Math.round(v).toLocaleString()}`;
    }
  };

  return (
    <section data-testid="mortgage-calc" className="mt-10 hairline bg-white p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 grid place-items-center bg-black text-white">
          <Calculator className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <h3 className="font-display text-xl font-medium">{t(lang, "mortgageTitle")}</h3>
      </div>
      <form onSubmit={calc} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{t(lang, "mortgageLoan")}</label>
          <input
            data-testid="mortgage-loan"
            type="number" min="0" required
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{t(lang, "mortgageRate")}</label>
          <input
            data-testid="mortgage-rate"
            type="number" min="0" step="0.01" required
            value={rate} onChange={(e) => setRate(e.target.value)}
            className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none font-mono"
          />
          <p className="text-[10px] text-muted-foreground mt-1.5 italic">{t(lang, "mortgageNote")}</p>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{t(lang, "mortgageTerm")}</label>
          <input
            data-testid="mortgage-term"
            type="number" min="1" max="40" required
            value={years} onChange={(e) => setYears(e.target.value)}
            className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none font-mono"
          />
        </div>
        <div className="sm:col-span-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-2">
          <button
            data-testid="mortgage-calc-btn" type="submit"
            className="px-6 py-3 bg-black text-white font-bold tracking-wide hover:opacity-90 flex items-center justify-center gap-2"
          >
            <Calculator className="w-4 h-4" /> {t(lang, "mortgageCalc")}
          </button>
          {monthly !== null && (
            <div data-testid="mortgage-result" className="flex-1 hairline bg-[var(--paper-2)] px-5 py-3 flex items-center justify-between gap-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{t(lang, "mortgageMonthly")}</div>
              <div className="font-display text-2xl font-semibold tabular-nums">{fmtCur(monthly)}</div>
            </div>
          )}
        </div>
      </form>
    </section>
  );
}
